import React, { useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';

import Checkbox from 'components/DesignSystem/Checkbox';
import EntryIcon from 'components/FileViewer/EntryIcon';
import ExplorerIcon from 'components/FileViewer/ExplorerIcon';
import formatBytes from 'helper/formatBytes';
import {
  ExplorerNode,
  ROOT_LABEL,
  SortDir,
  SortKey,
  formatModified,
  pluralize,
} from 'helper/products/fileExplorer';

import './index.scss';

export interface EntryActions {
  onOpenFolder: (path: string[]) => void;
  onDownload: (node: ExplorerNode & { kind: 'file' }) => void;
  downloadingKeys: Set<string>;
  isFavorite: (key: string) => boolean;
  onTogglePin: (node: ExplorerNode & { kind: 'folder' }) => void;
}

interface EntryTableProps extends EntryActions {
  entries: ExplorerNode[];
  /** Busca global: cada linha mostra onde o item está. */
  showPath: boolean;
  /** Coluna de tamanho e data com hora; sem ela, o tamanho vai para baixo do nome. */
  wide: boolean;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  selectedKeys: Set<string>;
  onToggleSelected: (key: string) => void;
  allSelected: boolean;
  someSelected: boolean;
  hasSelectableFiles: boolean;
  onToggleAll: () => void;
}

export const entrySizeText = (node: ExplorerNode) => (
  node.kind === 'folder' ? pluralize(node.fileCount, 'arquivo', 'arquivos') : formatBytes(node.size)
);

export const entryModified = (node: ExplorerNode) => (
  node.kind === 'folder' ? node.latestModified : node.modified
);

export const entryLocation = (node: ExplorerNode) => (
  [ROOT_LABEL, ...(node.kind === 'folder' ? node.path.slice(0, -1) : node.path)].join(' / ')
);

function SortHeader({
  label, column, sortKey, sortDir, onSort, className,
}: {
  label: string;
  column: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  className: string;
}) {
  const active = sortKey === column;
  let ariaSort: 'ascending' | 'descending' | 'none' = 'none';
  if (active) ariaSort = sortDir === 1 ? 'ascending' : 'descending';
  // Coluna inativa mostra "↕": sem isso "Tamanho" e "Modificado" pareciam texto fixo.
  let arrow = '↕';
  if (active) arrow = sortDir === 1 ? '↑' : '↓';

  return (
    <div role="columnheader" aria-sort={ariaSort} className={className}>
      <button
        type="button"
        className={`file-explorer-sort${active ? ' is-active' : ''}`}
        onClick={() => onSort(column)}
      >
        {label}
        <span className={`file-explorer-sort-arrow${active ? '' : ' is-idle'}`} aria-hidden="true">
          {arrow}
        </span>
      </button>
    </div>
  );
}

/** Ação da linha: estrela (pasta) ou baixar (arquivo). Compartilhada com a lista mobile. */
export function RowAction({
  node, actions, size = 'md',
}: {
  node: ExplorerNode;
  actions: EntryActions;
  size?: 'md' | 'lg';
}) {
  if (node.kind === 'folder') {
    const pinned = actions.isFavorite(node.key);
    return (
      <button
        type="button"
        className={`file-explorer-pin${pinned ? ' is-pinned' : ''} is-${size}`}
        aria-label={pinned ? `Remover “${node.name}” do acesso rápido` : `Fixar “${node.name}” no acesso rápido`}
        aria-pressed={pinned}
        title={pinned ? 'Remover do acesso rápido' : 'Fixar no acesso rápido'}
        onClick={() => actions.onTogglePin(node)}
      >
        <ExplorerIcon name="star" size={size === 'lg' ? 18 : 16} strokeWidth={1.8} filled={pinned} />
      </button>
    );
  }

  const downloading = actions.downloadingKeys.has(node.key);
  return (
    <button
      type="button"
      className={`file-explorer-download is-${size}`}
      aria-label={`Baixar ${node.name}`}
      title="Baixar"
      disabled={downloading}
      onClick={() => actions.onDownload(node)}
    >
      {downloading
        ? <Spinner animation="border" size="sm" role="presentation" />
        : <ExplorerIcon name="download" size={size === 'lg' ? 18 : 15} strokeWidth={2.2} />}
    </button>
  );
}

function SelectAllCheckbox({
  checked, indeterminate, onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <Checkbox
      ref={ref}
      aria-label="Selecionar todos os arquivos"
      checked={checked}
      onChange={onChange}
      className="file-explorer-checkbox"
    />
  );
}

export default function EntryTable({
  entries, showPath, wide, sortKey, sortDir, onSort,
  selectedKeys, onToggleSelected, allSelected, someSelected, hasSelectableFiles, onToggleAll,
  ...actions
}: EntryTableProps) {
  return (
    <div role="table" aria-label="Conteúdo da pasta" className={`file-explorer-table ${wide ? 'is-wide' : 'is-narrow'}`}>
      <div role="rowgroup">
        <div role="row" className="file-explorer-row is-head">
          <div role="columnheader" className="file-explorer-cell-check">
            {hasSelectableFiles && (
              <SelectAllCheckbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={onToggleAll}
              />
            )}
          </div>
          <div role="columnheader" className="file-explorer-cell-icon">
            <span className="visually-hidden">Tipo</span>
          </div>
          <SortHeader
            label="Nome"
            column="name"
            className="file-explorer-cell-name"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
          />
          {wide && (
            <SortHeader
              label="Tamanho"
              column="size"
              className="file-explorer-cell-size"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
            />
          )}
          <SortHeader
            label="Modificado"
            column="date"
            className="file-explorer-cell-date"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
          />
          <div role="columnheader" className="file-explorer-cell-actions">
            <span className="visually-hidden">Ações</span>
          </div>
        </div>
      </div>
      <div role="rowgroup">
        {entries.map((node) => {
          const isFile = node.kind === 'file';
          const selected = isFile && selectedKeys.has(node.key);
          const open = () => (node.kind === 'folder' ? actions.onOpenFolder(node.path) : actions.onDownload(node));
          const size = entrySizeText(node);
          /* eslint-disable jsx-a11y/click-events-have-key-events,
             jsx-a11y/interactive-supports-focus --
             O clique na linha é atalho de mouse. Pelo teclado, a mesma ação está no botão do
             nome (abrir/baixar) e no checkbox (selecionar), ambos focáveis dentro da linha. */
          return (
            <div
              key={node.key}
              role="row"
              className={`file-explorer-row is-${node.kind}${selected ? ' is-selected' : ''}`}
              onClick={(event) => {
                // Clique no "vazio" da linha: pasta abre, arquivo marca/desmarca.
                if ((event.target as HTMLElement).closest('button, input, label, a')) return;
                if (isFile) onToggleSelected(node.key);
                else open();
              }}
            >
              <div role="cell" className="file-explorer-cell-check">
                {isFile && (
                  <Checkbox
                    aria-label={`Selecionar ${node.name}`}
                    checked={selected}
                    onChange={() => onToggleSelected(node.key)}
                    className="file-explorer-checkbox"
                  />
                )}
              </div>
              <div role="cell" className="file-explorer-cell-icon">
                <EntryIcon node={node} />
              </div>
              <div role="cell" className="file-explorer-cell-name">
                <button
                  type="button"
                  className="file-explorer-entry-name"
                  title={isFile ? `Baixar ${node.name}` : `Abrir ${node.name}`}
                  onClick={open}
                >
                  {node.name}
                </button>
                {showPath && <div className="file-explorer-entry-path">{entryLocation(node)}</div>}
                {!wide && <div className="file-explorer-entry-meta">{size}</div>}
              </div>
              {wide && <div role="cell" className="file-explorer-cell-size">{size}</div>}
              <div role="cell" className="file-explorer-cell-date">
                {formatModified(entryModified(node), wide)}
              </div>
              <div role="cell" className="file-explorer-cell-actions">
                <RowAction node={node} actions={actions} />
              </div>
            </div>
          );
          /* eslint-enable jsx-a11y/click-events-have-key-events,
             jsx-a11y/interactive-supports-focus */
        })}
      </div>
    </div>
  );
}
